export type CmrRequirementSeverity = 'error' | 'warning';
export type CmrRequirementApplicability = 'mandatory' | 'conditional';

export interface CmrRequirementSource {
  document: 'CMR_1956.txt' | 'CMR_2008.txt';
  article: string;
  reference: string;
}

export interface CmrRequirement {
  id: string;
  article: string;
  label: string;
  severity: CmrRequirementSeverity;
  applicability: CmrRequirementApplicability;
  fieldPaths: string[];
  source: CmrRequirementSource;
}

const source = (article: string): CmrRequirementSource => ({
  document: 'CMR_1956.txt',
  article,
  reference: 'Article 6, docs/regulations/CMR_1956.txt:101-139',
});

const article2Source = (): CmrRequirementSource => ({
  document: 'CMR_1956.txt',
  article: '2',
  reference: 'Article 2, docs/regulations/CMR_1956.txt:55-57',
});

export const CMR_ARTICLE_6_1_REQUIREMENT_IDS = [
  'CMR-6.1-a-date-place',
  'CMR-6.1-b-sender',
  'CMR-6.1-c-carrier',
  'CMR-6.1-d-taking-over-delivery',
  'CMR-6.1-e-consignee',
  'CMR-6.1-f-goods-packing',
  'CMR-6.1-g-packages-marks',
  'CMR-6.1-h-weight-quantity',
  'CMR-6.1-i-charges',
  'CMR-6.1-j-customs-formalities',
  'CMR-6.1-k-cmr-statement',
] as const;

export const mandatoryCmrRequirements: CmrRequirement[] = [
  {
    id: 'CMR-6.1-a-date-place',
    article: '6.1(a)',
    label: 'Date of the consignment note and place where it is made out',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: [
      'ExchangedDocument.IssueDateTime.DateTimeString',
      'ExchangedDocument.IssueLogisticsLocation',
    ],
    source: source('6.1(a)'),
  },
  {
    id: 'CMR-6.1-b-sender',
    article: '6.1(b)',
    label: 'Name and address of the sender',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: ['SpecifiedSupplyChainConsignment.ConsignorTradeParty'],
    source: source('6.1(b)'),
  },
  {
    id: 'CMR-6.1-c-carrier',
    article: '6.1(c)',
    label: 'Name and address of the carrier',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: ['SpecifiedSupplyChainConsignment.CarrierTradeParty'],
    source: source('6.1(c)'),
  },
  {
    id: 'CMR-6.1-d-taking-over-delivery',
    article: '6.1(d)',
    label: 'Place and date of taking over the goods and place designated for delivery',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: [
      'SpecifiedSupplyChainConsignment.CarrierAcceptanceLogisticsLocation',
      'SpecifiedSupplyChainConsignment.PickUpTransportEvent',
      'SpecifiedSupplyChainConsignment.ConsigneeReceiptLogisticsLocation',
      'SpecifiedSupplyChainConsignment.DeliveryTransportEvent',
    ],
    source: source('6.1(d)'),
  },
  {
    id: 'CMR-6.1-e-consignee',
    article: '6.1(e)',
    label: 'Name and address of the consignee',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: ['SpecifiedSupplyChainConsignment.ConsigneeTradeParty'],
    source: source('6.1(e)'),
  },
  {
    id: 'CMR-6.1-f-goods-packing',
    article: '6.1(f)',
    label:
      'Common description of the nature of the goods, method of packing, and generally recognized description for dangerous goods where applicable',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: ['SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem'],
    source: source('6.1(f)'),
  },
  {
    id: 'CMR-6.1-g-packages-marks',
    article: '6.1(g)',
    label: 'Number of packages and their special marks and numbers',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: [
      'SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem.TransportLogisticsPackage',
    ],
    source: source('6.1(g)'),
  },
  {
    id: 'CMR-6.1-h-weight-quantity',
    article: '6.1(h)',
    label: 'Gross weight of the goods or their quantity otherwise expressed',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: [
      'SpecifiedSupplyChainConsignment.GrossWeightMeasure',
      'SpecifiedSupplyChainConsignment.ConsignmentItemQuantity',
      'SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem.GrossWeightMeasure',
      'SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem.ConsignmentItemQuantity',
    ],
    source: source('6.1(h)'),
  },
  {
    id: 'CMR-6.1-i-charges',
    article: '6.1(i)',
    label: 'Charges relating to the carriage',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: [
      'SpecifiedSupplyChainConsignment.ApplicableLogisticsServiceCharge.AppliedAmount',
      'SpecifiedSupplyChainConsignment.SpecifiedTradeAllowanceCharge.ActualAmount',
      'SpecifiedSupplyChainConsignment.carriageCharges',
      'SpecifiedSupplyChainConsignment.charges',
    ],
    source: source('6.1(i)'),
  },
  {
    id: 'CMR-6.1-j-customs-formalities',
    article: '6.1(j)',
    label: 'Instructions required for Customs and other formalities',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: [
      'SpecifiedSupplyChainConsignment.ConsignorProvidedBorderClearanceTransportInstructions',
      'SpecifiedSupplyChainConsignment.AssociatedReferencedDocument',
      'SpecifiedSupplyChainConsignment.PreviousAdministrativeReferencedDocument',
      'SpecifiedSupplyChainConsignment.customsInstructions',
    ],
    source: source('6.1(j)'),
  },
  {
    id: 'CMR-6.1-k-cmr-statement',
    article: '6.1(k)',
    label: 'Statement that carriage is subject to the CMR Convention',
    severity: 'error',
    applicability: 'mandatory',
    fieldPaths: ['ExchangedDocument.IncludedNote', 'ExchangedDocument.Remarks'],
    source: source('6.1(k)'),
  },
];

export const selectedOptionalCmrRequirements: CmrRequirement[] = [
  {
    id: 'CMR-6.2-a-transshipment-prohibited',
    article: '6.2(a)',
    label: 'Statement that transshipment is not allowed, where applicable',
    severity: 'error',
    applicability: 'conditional',
    fieldPaths: ['SpecifiedSupplyChainConsignment.TransshipmentPermissionIndicator'],
    source: source('6.2(a)'),
  },
  {
    id: 'CMR-6.2-b-sender-paid-charges',
    article: '6.2(b)',
    label: 'Charges the sender undertakes to pay, where applicable',
    severity: 'error',
    applicability: 'conditional',
    fieldPaths: ['SpecifiedSupplyChainConsignment.ApplicableLogisticsServiceCharge'],
    source: source('6.2(b)'),
  },
  {
    id: 'CMR-6.2-c-cash-on-delivery',
    article: '6.2(c)',
    label: 'Cash on delivery amount, where applicable',
    severity: 'error',
    applicability: 'conditional',
    fieldPaths: ['SpecifiedSupplyChainConsignment.CODAmount'],
    source: source('6.2(c)'),
  },
  {
    id: 'CMR-6.2-d-declared-value-special-interest',
    article: '6.2(d)',
    label: 'Declared value and special interest in delivery, where applicable',
    severity: 'error',
    applicability: 'conditional',
    fieldPaths: [
      'SpecifiedSupplyChainConsignment.AssociatedInvoiceAmount',
      'SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem.InvoiceAmount',
    ],
    source: source('6.2(d)'),
  },
  {
    id: 'CMR-6.2-e-insurance-instructions',
    article: '6.2(e)',
    label: 'Sender insurance instructions to the carrier, where applicable',
    severity: 'error',
    applicability: 'conditional',
    fieldPaths: ['SpecifiedSupplyChainConsignment.CargoInsuranceInstructionsInformation'],
    source: source('6.2(e)'),
  },
  {
    id: 'CMR-6.2-f-agreed-time-limit',
    article: '6.2(f)',
    label: 'Agreed time limit for carrying out the carriage, where applicable',
    severity: 'error',
    applicability: 'conditional',
    fieldPaths: [
      'SpecifiedSupplyChainConsignment.DeliveryInstructions',
      'SpecifiedSupplyChainConsignment.DeliveryTransportEvent',
    ],
    source: source('6.2(f)'),
  },
  {
    id: 'CMR-6.2-g-documents-handed-to-carrier',
    article: '6.2(g)',
    label: 'Documents handed to the carrier, where applicable',
    severity: 'error',
    applicability: 'conditional',
    fieldPaths: ['SpecifiedSupplyChainConsignment.AssociatedReferencedDocument'],
    source: source('6.2(g)'),
  },
];

export const conditionalCmrArticle2Requirements: CmrRequirement[] = [
  {
    id: 'CMR-2-multimodal-vehicle-carriage',
    article: '2',
    label: 'Vehicle carried by sea, rail, inland waterways or air with goods remaining loaded unless Article 14 applies',
    severity: 'error',
    applicability: 'conditional',
    fieldPaths: ['SpecifiedSupplyChainConsignment.CmrArticle2MultimodalCarriage'],
    source: article2Source(),
  },
];

export const cmrRequirements: CmrRequirement[] = [
  ...conditionalCmrArticle2Requirements,
  ...mandatoryCmrRequirements,
  ...selectedOptionalCmrRequirements,
];
